/**
 * 本地日期工具（与 iOS LocalCalendar 一致）：所有本地日期 / 时间都按家庭时区 family.timezone 计算，
 * 不用浏览器所在的时区。日期字符串 YYYY-MM-DD，时间 HH:mm，时间戳为 UTC 毫秒。
 */

const DEFAULT_TZ = "Asia/Shanghai";

export const WEEKDAY_NAMES = ["", "一", "二", "三", "四", "五", "六", "日"] as const;

interface Parts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(tz: string): Intl.DateTimeFormat {
  let f = formatters.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
    formatters.set(tz, f);
  }
  return f;
}

function validTimeZone(tz: string | null | undefined): string {
  if (!tz) return DEFAULT_TZ;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return tz;
  } catch {
    return DEFAULT_TZ;
  }
}

const pad = (n: number) => String(n).padStart(2, "0");

/** "2026-10-19" → [2026, 10, 19]；格式不对返回 null */
function parseDate(s: string): [number, number, number] | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

function fromUtcDay(ms: number): string {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

export class LocalCalendar {
  readonly timeZone: string;

  constructor(timezone?: string | null) {
    this.timeZone = validTimeZone(timezone);
  }

  /** 时间戳在家庭时区的年月日时分秒 */
  parts(at: Date | number = Date.now()): Parts {
    const ms = typeof at === "number" ? at : at.getTime();
    const p: Record<string, number> = {};
    for (const x of partsFormatter(this.timeZone).formatToParts(new Date(ms))) {
      if (x.type !== "literal") p[x.type] = Number(x.value);
    }
    return { year: p.year!, month: p.month!, day: p.day!, hour: p.hour! % 24, minute: p.minute!, second: p.second! };
  }

  /** 时区偏移（毫秒）：本地时间 - UTC */
  offsetMs(at: number): number {
    const p = this.parts(at);
    const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    return asUtc - Math.floor(at / 1000) * 1000;
  }

  /** YYYY-MM-DD */
  dateString(at: Date | number = Date.now()): string {
    const p = this.parts(at);
    return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
  }

  /** HH:mm */
  timeString(at: Date | number): string {
    const p = this.parts(at);
    return `${pad(p.hour)}:${pad(p.minute)}`;
  }

  today(): string {
    return this.dateString(Date.now());
  }

  /** 日期加减天数（纯日历运算，与时区无关） */
  addDays(date: string, n: number): string {
    const d = parseDate(date);
    if (!d) return date;
    return fromUtcDay(Date.UTC(d[0], d[1] - 1, d[2] + n));
  }

  /** 两个日期相差几天（b - a） */
  daysBetween(a: string, b: string): number {
    const da = parseDate(a);
    const db = parseDate(b);
    if (!da || !db) return 0;
    return Math.round((Date.UTC(db[0], db[1] - 1, db[2]) - Date.UTC(da[0], da[1] - 1, da[2])) / 86_400_000);
  }

  /** 周几：1=周一 … 7=周日 */
  weekday(date: string): number {
    const d = parseDate(date);
    if (!d) return 1;
    const w = new Date(Date.UTC(d[0], d[1] - 1, d[2])).getUTCDay(); // 0=周日
    return w === 0 ? 7 : w;
  }

  /** "周一" */
  weekdayName(date: string): string {
    return `周${WEEKDAY_NAMES[this.weekday(date)]}`;
  }

  /** 这一周的周一 */
  mondayOf(date: string): string {
    return this.addDays(date, -(this.weekday(date) - 1));
  }

  /** 这个月 1 号 */
  monthStart(date: string): string {
    const d = parseDate(date);
    return d ? `${d[0]}-${pad(d[1])}-01` : date;
  }

  /** 这个月有几天 */
  daysInMonth(date: string): number {
    const d = parseDate(date);
    return d ? new Date(Date.UTC(d[0], d[1], 0)).getUTCDate() : 30;
  }

  /** 加减月份（日期超出目标月时取月末） */
  addMonths(date: string, n: number): string {
    const d = parseDate(date);
    if (!d) return date;
    const first = new Date(Date.UTC(d[0], d[1] - 1 + n, 1));
    const max = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
    return `${first.getUTCFullYear()}-${pad(first.getUTCMonth() + 1)}-${pad(Math.min(d[2], max))}`;
  }

  /** "10月19日" */
  monthDay(date: string): string {
    const d = parseDate(date);
    return d ? `${d[1]}月${d[2]}日` : date;
  }

  /** "2026年10月" */
  yearMonth(date: string): string {
    const d = parseDate(date);
    return d ? `${d[0]}年${d[1]}月` : date;
  }

  /** 本地日期 + HH:mm 转为时间戳（按家庭时区，处理夏令时）；格式不对返回 null */
  ms(date: string, time: string): number | null {
    const d = parseDate(date);
    const t = /^(\d{1,2}):(\d{2})$/.exec(time);
    if (!d || !t) return null;
    const wall = Date.UTC(d[0], d[1] - 1, d[2], Number(t[1]), Number(t[2]));
    let ts = wall - this.offsetMs(wall);
    // 偏移在目标时刻可能不同（夏令时切换），再校正一次
    const off2 = this.offsetMs(ts);
    if (wall - off2 !== ts) ts = wall - off2;
    return ts;
  }

  /** 当天 0 点的时间戳 */
  startOfDay(date: string): number | null {
    return this.ms(date, "00:00");
  }

  /** 问候语 */
  greeting(at: Date | number = Date.now()): string {
    const h = this.parts(at).hour;
    if (h >= 5 && h < 11) return "早上好";
    if (h >= 11 && h < 13) return "中午好";
    if (h >= 13 && h < 18) return "下午好";
    return "晚上好";
  }

  /** "今天" / "昨天" / "明天" / "10月17日" */
  relativeDay(date: string): string {
    const t = this.today();
    if (date === t) return "今天";
    if (date === this.addDays(t, -1)) return "昨天";
    if (date === this.addDays(t, 1)) return "明天";
    return this.monthDay(date);
  }
}
